import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { formatDate as formatDateUtil } from "../utils/dateUtils";
import {
  getAllDocuments,
  updateDocument,
  uploadDocument,
} from "../redux/actions/documentActions";

import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Autocomplete,
  FormControl,
  Modal,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  DialogTitle,
  Select,
  InputLabel,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PreviewIcon from "@mui/icons-material/Preview";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Checkbox } from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { fetchFiles } from "../redux/actions/fileActions";
import { DOCUMENT_UPDATE_RESET } from "../redux/constants/documentConstants";
import { getAllFolders, createFolder, reorderFolders } from "../redux/actions/folderActions";
import { FOLDER_CREATE_RESET, FOLDER_REORDER_RESET } from "../redux/constants/folderConstants";
import { actionButtonStyles } from "../styles/actionButton";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { textFieldStyles } from "../styles/textFieldStyles";
import FileAutocomplete from "../components/FileAutocomplete";
import CustomAutocomplete from "../components/CustomAutocomplete";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MicrosoftWordIcon from "../styles/MicrosoftWordIcon";
import { smallTitle } from "../styles/typography";
import Avatar from "@mui/material/Avatar";
import ImageIcon from '@mui/icons-material/Image';
import ExcelIcon from '../styles/ExcelIcon';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ClickAwayListener from '@mui/material/ClickAwayListener';

const tableStyles = windowsTableStyles();

// SixDots component for drag handles
const SixDots = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, auto)",
      gap: "4px",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    {[...Array(6)].map((_, index) => (
      <Box
        key={index}
        sx={{
          width: "3px",
          height: "3px",
          backgroundColor: "grey",
          borderRadius: "50%",
        }}
      />
    ))}
  </Box>
);

// TreeConnector component for visual hierarchy
const TreeConnector = ({ isLast = false, hasChildren = false, isExpanded = false }) => (
  <Box
    sx={{
      position: "relative",
      width: "20px",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mr: 0.5,
    }}
  >
    {/* Vertical line */}
    {!isLast && (
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          bottom: "-50%",
          width: "1px",
          backgroundColor: "#d1d1d1",
          transform: "translateX(-50%)",
        }}
      />
    )}
    {/* Horizontal line */}
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: "10px",
        height: "1px",
        backgroundColor: "#d1d1d1",
        transform: "translateY(-50%)",
      }}
    />
    {/* Connection dot */}
    <Box
      sx={{
        width: "4px",
        height: "4px",
        backgroundColor: "#2B80B6",
        borderRadius: "50%",
        zIndex: 1,
      }}
    />
  </Box>
);

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 0
};

const DocumentList = (props) => {
  const { t, i18n } = useTranslation(['pages', 'common', 'buttons', 'forms']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { id: currentFileId } = useParams();

  // Helper function to get translated folder name
  const getTranslatedFolderName = (folderName, isDefault = false) => {
    if (!isDefault) return folderName;
    
    switch (folderName) {
      case 'Financial':
        return t('forms:folders.financial');
      case 'Correspondence':
        return t('forms:folders.correspondence');
      case 'Medical':
        return t('forms:folders.medical');
      case 'Other':
        return t('forms:folders.other');
      default:
        return folderName;
    }
  };

  // Redux State
  const { files } = useSelector((state) => state.fileList);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { user } = useSelector((state) => state.userLoaded);
  const { documents, loading, error } = useSelector((state) => state.documentList);
  const { success: updateSuccess } = useSelector((state) => state.documentUpdate);
  const { folders } = useSelector((state) => state.folderList);
  const { success: folderCreateSuccess } = useSelector((state) => state.folderCreate);
  const { success: folderReorderSuccess, error: folderReorderError } = useSelector((state) => state.folderReorder);
  const currentLanguage = useSelector((state) => state.language?.currentLanguage);

  // Component State
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterOwner, setFilterOwner] = useState(null);
  const [filterOwnerCleared, setFilterOwnerCleared] = useState(false);
  const [filterCaseFile, setFilterCaseFile] = useState(null);
  const [order] = useState("asc");
  const [orderBy] = useState("title");

  // New state for hierarchical interface
  const [expandedUsers, setExpandedUsers] = useState({});
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [addingFolderForUser, setAddingFolderForUser] = useState(null);

  // Sorting state for documents within folders
  const [sortConfig, setSortConfig] = useState({ key: 'modifiedDate', direction: 'desc' });

  // Document selection state for bulk operations
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // New state for menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSelectedDocument, setMenuSelectedDocument] = useState(null);
  
  // State for upload menu
  const [uploadMenuAnchorEl, setUploadMenuAnchorEl] = useState(null);

  // New state for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // New state for inline editing
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [editingDescription, setEditingDescription] = useState("");

  // Local state to track folder order for each user
  const [folderOrderByUser, setFolderOrderByUser] = useState({});

  // Drag-and-drop upload state for documents table
  const [dragOverTable, setDragOverTable] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDialogCaseFile, setUploadDialogCaseFile] = useState(null);
  const [uploadDialogFolder, setUploadDialogFolder] = useState('Other');
  const [uploadingFromDrop, setUploadingFromDrop] = useState(false);

  // Add state for folder delete dialog
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  // Move document state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [documentToMove, setDocumentToMove] = useState(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState("");
  const [availableMoveFolders, setAvailableMoveFolders] = useState([]);

  // Preview sidebar state
  const [previewSidebarOpen, setPreviewSidebarOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const previewSidebarRef = useRef(null);

  // const loggedInUser = relationships.find(relationship => relationship.user?._id === user?._id);
  const loggedInUser = user?.relationship;

  console.log('loggedInUser', loggedInUser);

  // Initial data fetch
  useEffect(() => {
    dispatch(getAllDocuments());
    dispatch(fetchFiles()); // Ensure files are fetched if not already present
    // Use currentFileId if on case file page, otherwise use filterCaseFile if selected
    const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
    dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId)); // Fetch all folders with document counts, filtered by case file if available
  }, [dispatch, currentLanguage, i18n.language, currentFileId, filterCaseFile]);
  
  // Refresh folders when case file filter changes
  useEffect(() => {
    const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
    dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId));
  }, [filterCaseFile, dispatch, currentLanguage, i18n.language, currentFileId]);
  
  // Auto-populate filter for all users (including Managers)
  useEffect(() => {
    // Set default filterOwner to the current user's relationship
    // Only set if the filter hasn't been manually cleared by the user
    if (user?.relationship && relationships) {
      const userRelationship = relationships.find(rel => rel._id === user.relationship);
      if (userRelationship) {
        setFilterOwner(userRelationship);
      }
    }
  }, [user, relationships]); // Rerun if user or relationships changes

  // Filter files based on user role for uploads (reuse logic from DocumentUpload)
  const filteredFilesForUpload = (files || []).filter((file) => {
    if (user?.role === "Manager") {
      return true; // Managers can see all files
    } else if (user?.role === "Responsible Lawyer") {
      return file.officeMembers?.some(
        (member) =>
          member.member?._id === user?.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user?.role === "Assistant Lawyer") {
      return file.officeMembers?.some(
        (member) =>
          member.member?._id === user?.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });



  // Handle notifications for document updates
  useEffect(() => {
    if (updateSuccess) {
      setNotification({
        open: true,
        message: t('pages:documents.document_updated'),
        severity: "success",
      });
      dispatch({ type: DOCUMENT_UPDATE_RESET });
    }
  }, [updateSuccess, dispatch]);

  // Handle folder creation success
  useEffect(() => {
    if (folderCreateSuccess) {
      setNotification({
        open: true,
        message: t('pages:documents.folder_created'),
        severity: "success",
      });
      dispatch({ type: FOLDER_CREATE_RESET });
      // Refresh folders list to ensure we have the latest data
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId));
    }
  }, [folderCreateSuccess, dispatch, currentLanguage, i18n.language]);

  // Handle folder reorder success/error
  useEffect(() => {
    if (folderReorderSuccess) {
      console.log('✅ Folder reorder success detected in useEffect');
      setNotification({
        open: true,
        message: t('pages:documents.folders_reordered'),
        severity: "success",
      });
      dispatch({ type: FOLDER_REORDER_RESET });
      // Refresh folders list to get the updated order
      console.log('🔄 Dispatching getAllFolders to refresh folder list');
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId));
    } else if (folderReorderError) {
      console.error('❌ Folder reorder error detected:', folderReorderError);
      setNotification({
        open: true,
        message: folderReorderError,
        severity: "error",
      });
      dispatch({ type: FOLDER_REORDER_RESET });
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId));
    }
  }, [folderReorderSuccess, folderReorderError, dispatch]);

  // Clear document selection when folder changes
  useEffect(() => {
    setSelectedDocuments([]);
  }, [selectedFolder, selectedUser]);

  // On reorder error, refresh folders from backend to sync local state (like page refresh)
  useEffect(() => {
    if (folderReorderError) {
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId));
    }
  }, [folderReorderError, dispatch, currentLanguage, i18n.language, currentFileId]);

  // When folders are refreshed from backend (e.g., after reload or after getAllFolders), always update local state to match backend
  useEffect(() => {
    if (folders && folders.length > 0) {
      // Sort folders by sortOrder before building the order state
      const sortedFolders = [...folders].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      const newOrder = {};
      sortedFolders.forEach(folder => {
        const userId = folder.owner._id;
        if (!newOrder[userId]) newOrder[userId] = [];
        if (!newOrder[userId].includes(folder._id)) newOrder[userId].push(folder._id);
      });
      setFolderOrderByUser(newOrder);
    }
  }, [folders]);

  // Event Handlers
  const handleDownload = async (documentId, fileName) => {
    try {
      const { data } = await axios.get(`/api/documents/download/${documentId}`);
      if (data) {
        const response = await fetch(data.fileUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      setNotification({ open: true, message: t('pages:documents.error_downloading'), severity: 'error' });
    }
  };

  const handleView = async (documentId, documentType) => {
    try {
      const { data } = await axios.get(`/api/documents/download/${documentId}`);
      if (data) {
        if (documentType.startsWith('image')) {
          const newWindow = window.open(data.fileUrl, '_blank', 'noopener,noreferrer');
          if (newWindow) newWindow.opener = null;
        } else {
          const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(data.fileUrl)}&embedded=true`;
          const newWindow = window.open(viewerUrl, '_blank', 'noopener,noreferrer');
          if (newWindow) newWindow.opener = null;
        }
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      setNotification({ open: true, message: t('pages:documents.error_viewing'), severity: 'error' });
    }
  };

  const handlePreviewDocument = async (document, isRefresh = false) => {
    if (!document) return;
    
    // Toggle sidebar if same document is clicked (but not on refresh)
    if (!isRefresh && previewSidebarOpen && previewDocument?._id === document._id) {
      setPreviewSidebarOpen(false);
      setPreviewDocument(null);
      setPreviewUrl(null);
      setPreviewError(false);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(false);
    
    // Only set document and open sidebar if not refreshing
    if (!isRefresh) {
      setPreviewDocument(document);
      setPreviewSidebarOpen(true);
    }

    try {
      const { data } = await axios.get(`/api/documents/download/${document._id}`);
      if (data && data.fileUrl) {
        if (document.type && document.type.startsWith('image')) {
          // For images, use direct URL (no delay needed)
          setPreviewUrl(data.fileUrl);
        } else {
          // For documents in iframe, delay loading until sidebar animation completes (350ms)
          // This ensures iframe initializes with correct dimensions for proper zoom/controls rendering
          // On refresh, clear URL first to force iframe reload
          if (isRefresh) {
            setPreviewUrl(null);
            setTimeout(() => {
              const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(data.fileUrl)}&embedded=true`;
              setPreviewUrl(viewerUrl);
            }, 100);
          } else {
            setTimeout(() => {
              const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(data.fileUrl)}&embedded=true`;
              setPreviewUrl(viewerUrl);
            }, 350); // Match sidebar transition duration (300ms) + buffer
          }
        }
      } else {
        setPreviewError(true);
      }
    } catch (error) {
      console.error('Error loading document preview:', error);
      setPreviewError(true);
      setNotification({ open: true, message: t('pages:documents.error_viewing'), severity: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle refresh preview
  const handleRefreshPreview = () => {
    if (previewDocument) {
      handlePreviewDocument(previewDocument, true); // Pass isRefresh = true
    }
  };

  // Auto-scroll horizontally to preview sidebar when it opens
  useEffect(() => {
    if (previewSidebarOpen && previewSidebarRef.current) {
      // Small delay to ensure DOM is updated and sidebar width transition completes
      setTimeout(() => {
        // Find the scrollable parent container (the main flex container with overflowX: auto)
        const scrollableContainer = previewSidebarRef.current?.closest('[style*="overflowX"]') || 
                                    previewSidebarRef.current?.parentElement?.parentElement;
        
        if (scrollableContainer && scrollableContainer.scrollWidth > scrollableContainer.clientWidth) {
          // Get sidebar position relative to scrollable container
          const sidebarRect = previewSidebarRef.current.getBoundingClientRect();
          const containerRect = scrollableContainer.getBoundingClientRect();
          
          // Calculate sidebar position relative to container
          const sidebarRightRelative = sidebarRect.right - containerRect.left;
          const containerWidth = containerRect.width;
          const currentScrollLeft = scrollableContainer.scrollLeft;
          
          // If sidebar extends beyond visible area, scroll to show it
          if (sidebarRightRelative > containerWidth) {
            const scrollAmount = sidebarRightRelative - containerWidth + 20; // 20px padding
            scrollableContainer.scrollTo({
              left: currentScrollLeft + scrollAmount,
              behavior: 'smooth'
            });
          } else if (sidebarRect.left < containerRect.left) {
            // If sidebar is off-screen to the left
            const scrollAmount = sidebarRect.left - containerRect.left - 20; // 20px padding
            scrollableContainer.scrollTo({
              left: currentScrollLeft + scrollAmount,
              behavior: 'smooth'
            });
          }
        } else {
          // Fallback: scroll window horizontally if container doesn't scroll
          const sidebarRect = previewSidebarRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          
          if (sidebarRect.right > viewportWidth) {
            window.scrollTo({
              left: window.scrollX + (sidebarRect.right - viewportWidth) + 20,
              top: window.scrollY, // Keep vertical position unchanged
              behavior: 'smooth'
            });
          }
        }
      }, 350); // Wait for transition to complete (300ms + 50ms buffer)
    }
  }, [previewSidebarOpen]);

  const handleSendEmail = async (documentId) => {
    try {
      const { data } = await axios.get(`/api/documents/download/${documentId}`);
      const document = documents.find(doc => doc._id === documentId);
      if (data) {
        const fileResponse = await axios.get(data.fileUrl, { responseType: "blob" });
        const originalName = document.s3Key.split('_').slice(1).join('_');
        const file = new File([fileResponse.data], originalName, { type: fileResponse.data.type });
        const base64Content = await convertFileToBase64(file);
        navigate("/send-email", {
          state: {
            attachments: [{ file, base64Content, name: originalName }],
            caseId: document.caseFile?._id,
            caseName: document.caseFile?.name
          }
        });
      }
    } catch (error) {
      console.error('Error handling email:', error);
      setNotification({ open: true, message: t('pages:documents.error_email'), severity: 'error' });
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUpdateDocument = (documentId) => {
    // Find the document object
    const document = documents.find(doc => doc._id === documentId);
    
    // Check if document was created from a template
    if (document?.templateId) {
      // Navigate to template page with pre-populated fields
      navigate('/settings', {
        state: {
          activeTab: 1, // Document Templates tab
          templateId: document.templateId,
          editDocumentId: documentId,
          editDocument: document // Pass full document object for pre-population
        }
      });
    } else {
      // Keep current file upload behavior for uploaded documents
      fileInputRef.current.click();
      fileInputRef.current.onchange = async (event) => {
        const file = event.target.files[0];
        if (file) {
          const formData = new FormData();
          formData.append("file", file);
          await dispatch(updateDocument(documentId, formData));
          // Refresh the document list after update
          dispatch(getAllDocuments());
        }
      };
    }
  };

  const handleCloseNotification = () => setNotification({ ...notification, open: false });
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleFilterTypeChange = (_, newValue) => setFilterType(newValue ? newValue.label : "");
  const handleFilterOwnerChange = (_, newValue) => {
    setFilterOwner(newValue || null);
    // Track if filter was manually cleared
    if (newValue === null) {
      setFilterOwnerCleared(true);
    } else {
      setFilterOwnerCleared(false);
    }
  };
  const handleFilterCaseFileChange = (_, newValue) => setFilterCaseFile(newValue || null);

  // New handlers for hierarchical interface
  const handleUserExpansion = (userId) => {
    const isExpanding = !expandedUsers[userId];
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
    // Auto-select "All" when expanding a user
    if (isExpanding && relationships) {
      const user = relationships.find(rel => rel._id === userId && rel.type === "Lawyer");
      if (user) {
        setSelectedUser(user);
        setSelectedFolder(null); // null means "All"
      }
    }
  };

  const handleFolderSelect = (folderName, user) => {
    // "ALL" means show all documents, so set folder to null
    setSelectedFolder(folderName === "ALL" ? null : folderName);
    setSelectedUser(user);
  };

  const handleAddFolder = (user) => {
    setAddingFolderForUser(user);
    setShowAddFolderModal(true);
  };

  const handleSaveNewFolder = async () => {
    if (!newFolderName.trim() || !addingFolderForUser) return;

    try {
      const folderData = {
        name: newFolderName,
        owner: addingFolderForUser._id
      };

      // If we're on a specific case file page or have a case file filter selected, include the caseFile
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      if (caseFileId) {
        folderData.caseFile = caseFileId;
      }

      dispatch(createFolder(folderData));

      setShowAddFolderModal(false);
      setNewFolderName("");
      setAddingFolderForUser(null);
    } catch (error) {
      setNotification({
        open: true,
        message: t('pages:documents.error_creating_folder'),
        severity: "error",
      });
    }
  };

  const handleCancelAddFolder = () => {
    setShowAddFolderModal(false);
    setNewFolderName("");
    setAddingFolderForUser(null);
  };

  // Sorting handler for documents within folders
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Drag and drop handler for folder reordering
  const handleFolderDragEnd = (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;
    const userId = result.source.droppableId.replace('folders-', '');
    const userFolders = (folderOrderByUser[userId] || []);
    const newOrder = Array.from(userFolders);
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(destinationIndex, 0, removed);
    setFolderOrderByUser(prev => ({ ...prev, [userId]: newOrder }));
    // Prepare folderOrders for API
    const folderOrders = newOrder.map((folderId, idx) => ({ folderId, sortOrder: idx }));
    dispatch(reorderFolders(folderOrders));
  };

  // Document selection handlers
  const handleDocumentSelect = (documentId) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === folderDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(folderDocuments.map(doc => doc._id));
    }
  };

  const handleClearSelection = () => {
    setSelectedDocuments([]);
  };

  // Bulk download handler
  const handleBulkDownload = async () => {
    if (selectedDocuments.length === 0) return;

    setIsDownloading(true);
    try {
      // Dynamic import of JSZip to avoid bundle size issues
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      let successCount = 0;
      let failCount = 0;

      // Download each selected document
      for (const documentId of selectedDocuments) {
        try {
          const document = folderDocuments.find(doc => doc._id === documentId);
          const { data } = await axios.get(`/api/documents/download/${documentId}`);

          if (data && data.fileUrl) {
            const response = await fetch(data.fileUrl);
            const blob = await response.blob();

            // Create unique filename to avoid conflicts
            const extension = document.type === 'pdf' ? '.pdf' :
                            document.type === 'word' ? '.docx' :
                            document.type === 'excel' ? '.xlsx' : '';
            const filename = `${document.title}${extension}`;

            zip.file(filename, blob);
            successCount++;
          }
        } catch (error) {
          console.error(`Error downloading document ${documentId}:`, error);
          failCount++;
        }
      }

      // Generate and download ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `documents-${selectedFolder}-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(zipUrl);

      // Show success message
      setNotification({
        open: true,
        message: t('pages:documents.download_success', { count: successCount, failed: failCount > 0 ? `, ${failCount} failed` : '' }),
        severity: successCount > 0 ? 'success' : 'error'
      });

      // Clear selection after successful download
      if (successCount > 0) {
        setSelectedDocuments([]);
      }
    } catch (error) {
      console.error('Error creating ZIP file:', error);
      setNotification({
        open: true,
        message: t('pages:documents.error_download_package'),
        severity: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Memoized Filtering Logic
  const filteredDocuments = React.useMemo(() => {
    if (!documents) return [];

    let filtered = documents.filter((document) => {
      const normalizeString = (str) => str?.replace(/\s+/g, "").toLowerCase() || "";
      const searchText = normalizeString(search);
      const ownerName = `${document.owner.firstName || ""} ${document.owner.middleName || ""} ${document.owner.lastName || ""}`;

      // If on a file overview page (currentFileId exists), only show documents for that file
      if (currentFileId && document.caseFile && document.caseFile._id !== currentFileId) {
        return false;
      }

      return (
        (normalizeString(document.title).includes(searchText) ||
         normalizeString(document.type).includes(searchText) ||
         normalizeString(document.folder).includes(searchText) ||
         normalizeString(document.caseFile.name).includes(searchText) ||
         normalizeString(ownerName).includes(searchText)) &&
        (!filterType || document.type === filterType) &&
        (!filterOwner || document.owner._id === filterOwner._id) &&
        (!filterCaseFile || document.caseFile._id === filterCaseFile._id)
      );
    });

    return filtered.sort((a, b) => {
      const isAsc = order === 'asc';
      if (orderBy === 'title') {
          return isAsc ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
      }
      if (orderBy === 'folder') {
          return isAsc ? a.folder.localeCompare(b.folder) : b.folder.localeCompare(a.folder);
      }
      if (orderBy === 'modifiedDate') {
          return isAsc ? new Date(a.modifiedDate) - new Date(b.modifiedDate) : new Date(b.modifiedDate) - new Date(a.modifiedDate);
      }
      // Add other sort conditions if necessary
      return 0;
    });
  }, [documents, search, filterType, filterOwner, filterCaseFile, order, orderBy, currentFileId]);

  // Format date to dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatDateUtil(dateString);
  };

  // Get unique users from filtered documents
  // const uniqueUsers = React.useMemo(() => {
  //   const userMap = new Map();
  //   filteredDocuments.forEach(document => {
  //     const userId = document.owner._id;
  //     if (!userMap.has(userId)) {
  //       userMap.set(userId, {
  //         ...document.owner,
  //         documentCount: 0
  //       });
  //     }
  //     userMap.get(userId).documentCount++;
  //   });
  //   return Array.from(userMap.values());
  // }, [filteredDocuments]);
  const uniqueUsers = React.useMemo(() => {
    // Ensure relationships and filteredDocuments are available
    if (!relationships || !Array.isArray(relationships)) {
      return [];
    }
    
    // Get all lawyer relationships as base users
    const allLawyers = relationships.filter((rel) => rel && rel.type === "Lawyer");
    
    // If filterOwner is selected, only show that user
    const baseUsers = filterOwner ? [filterOwner] : allLawyers;
    
    // Count documents for each user from filteredDocuments
    const userMap = new Map();
    
    // Initialize all base users with 0 document count
    baseUsers.forEach(lawyer => {
      if (lawyer && lawyer._id) {
        userMap.set(lawyer._id, {
          ...lawyer,
          documentCount: 0
        });
      }
    });
    
    // Add document counts from filtered documents
    if (filteredDocuments && Array.isArray(filteredDocuments)) {
      filteredDocuments.forEach(document => {
        if (document && document.owner && document.owner._id) {
          const userId = document.owner._id;
          if (userMap.has(userId)) {
            userMap.get(userId).documentCount++;
          }
        }
      });
    }
    
    return Array.from(userMap.values());
  }, [relationships, filteredDocuments, filterOwner]);

  // Get folders for each user (combining backend folders with document-based folders)
  const userFoldersMap = React.useMemo(() => {
    const userFolders = {};

    // Safety check: ensure uniqueUsers is available
    if (!uniqueUsers || uniqueUsers.length === 0) {
      return {};
    }

    // Define default folders that should always be shown
    const defaultFolders = [
      { name: 'Correspondence', displayName: t('forms:folders.correspondence'), isDefault: true, sortOrder: 0 },
      { name: 'Financial', displayName: t('forms:folders.financial'), isDefault: true, sortOrder: 1 },
      { name: 'Medical', displayName: t('forms:folders.medical'), isDefault: true, sortOrder: 2 },
      { name: 'Other', displayName: t('forms:folders.other'), isDefault: true, sortOrder: 3 }
    ];

    uniqueUsers.forEach(user => {
      const userDocs = filteredDocuments.filter(doc => doc.owner._id === user._id);

      // Get user's folders from backend
      const userBackendFolders = folders
        ? folders.filter(folder => folder.owner._id === user._id)
        : [];

      // Sort folders by local order state
      const orderedFolders = (folderOrderByUser[user._id] || [])
        .map(folderId => userBackendFolders.find(f => f._id === folderId))
        .filter(Boolean);

      // Get all backend folders: combine ordered with any missing backend folders
      const allBackendFolders = [...orderedFolders];
      userBackendFolders.forEach(folder => {
        if (!allBackendFolders.some(f => f._id === folder._id)) {
          allBackendFolders.push(folder);
        }
      });

      // Create complete folder list: start with default folders, then add custom folders
      const allFolders = [];

      // Add default folders (always show these, even if not in backend)
      defaultFolders.forEach(defaultFolder => {
        const backendFolder = allBackendFolders.find(f => f.name === defaultFolder.name);
        if (backendFolder) {
          // Use backend folder if it exists (preserves _id, sortOrder, etc.)
          allFolders.push(backendFolder);
        } else {
          // Create virtual folder for display purposes
          allFolders.push({
            name: defaultFolder.name,
            displayName: defaultFolder.displayName,
            isDefault: defaultFolder.isDefault,
            sortOrder: defaultFolder.sortOrder,
            owner: { _id: user._id },
            _id: `virtual-${defaultFolder.name}-${user._id}` // Virtual ID for consistency
          });
        }
      });

      // Add custom (non-default) folders from backend
      allBackendFolders.forEach(folder => {
        if (!defaultFolders.some(df => df.name === folder.name)) {
          allFolders.push(folder);
        }
      });

      // Sort all folders by sortOrder, then by name
      allFolders.sort((a, b) => {
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        if (a.sortOrder !== undefined) return -1;
        if (b.sortOrder !== undefined) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });

      // Now create userFolderMap with all folders
      const userFolderMap = {};
      allFolders.forEach(folder => {
        const folderKey = folder.displayName || folder.name;
        userFolderMap[folderKey] = {
          documents: [],
          folderObj: folder // Store the folder object for access during rendering
        };
      });

      // Then add documents to appropriate folders
      userDocs.forEach(doc => {
        const folder = allFolders.find(f => f.name === doc.folder);
        const folderKey = folder ? (folder.displayName || folder.name) : doc.folder;
        if (!userFolderMap[folderKey]) {
          // Create entry for folders that exist in documents but not in our folder list
          userFolderMap[folderKey] = {
            documents: [],
            folderObj: null // No folder object for document-only folders
          };
        }
        userFolderMap[folderKey].documents.push(doc);
      });

      userFolders[user._id] = userFolderMap;
    });
    return userFolders;
  }, [filteredDocuments, uniqueUsers, folders, folderOrderByUser, t]);

  // Get documents for selected folder with sorting
  const folderDocuments = React.useMemo(() => {
    // If user is selected but no folder (i.e., "All" option), show all documents for that user
    if (selectedUser && !selectedFolder) {
      const userDocs = filteredDocuments.filter(doc => doc.owner._id === selectedUser._id);
      return userDocs.sort((a, b) => {
        const { key, direction } = sortConfig;
        const isAsc = direction === 'asc';
        switch (key) {
          case 'title':
            return isAsc ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
          case 'originalFilename':
            return isAsc ? (a.originalFilename || '').localeCompare(b.originalFilename || '') : (b.originalFilename || '').localeCompare(a.originalFilename || '');
          case 'type':
            return isAsc ? a.type.localeCompare(b.type) : b.type.localeCompare(a.type);
          case 'modifiedDate':
            return isAsc ? new Date(a.modifiedDate) - new Date(b.modifiedDate) : new Date(b.modifiedDate) - new Date(a.modifiedDate);
          default:
            return 0;
        }
      });
    }
    
    // If no user selected, show all filtered documents
    if (!selectedUser) {
      // Apply sorting to all filtered documents
      return filteredDocuments.sort((a, b) => {
        const { key, direction } = sortConfig;
        const isAsc = direction === 'asc';
        switch (key) {
          case 'title':
            return isAsc ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
          case 'originalFilename':
            return isAsc ? (a.originalFilename || '').localeCompare(b.originalFilename || '') : (b.originalFilename || '').localeCompare(a.originalFilename || '');
          case 'type':
            return isAsc ? a.type.localeCompare(b.type) : b.type.localeCompare(a.type);
          case 'modifiedDate':
            return isAsc ? new Date(a.modifiedDate) - new Date(b.modifiedDate) : new Date(b.modifiedDate) - new Date(a.modifiedDate);
          default:
            return 0;
        }
      });
    }

    // Otherwise, show documents for selected folder/user
    // Use the documents from userFoldersMap for better performance
    const userFolders = userFoldersMap[selectedUser._id];
    if (!userFolders || !userFolders[selectedFolder]) {
      return [];
    }

    const docs = userFolders[selectedFolder].documents || [];

    // Apply sorting
    return docs.sort((a, b) => {
      const { key, direction } = sortConfig;
      const isAsc = direction === 'asc';
      switch (key) {
        case 'title':
          return isAsc ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
        case 'originalFilename':
          return isAsc ? (a.originalFilename || '').localeCompare(b.originalFilename || '') : (b.originalFilename || '').localeCompare(a.originalFilename || '');
        case 'type':
          return isAsc ? a.type.localeCompare(b.type) : b.type.localeCompare(a.type);
        case 'modifiedDate':
          return isAsc ? new Date(a.modifiedDate) - new Date(b.modifiedDate) : new Date(b.modifiedDate) - new Date(a.modifiedDate);
        default:
          return 0;
      }
    });
  }, [filteredDocuments, selectedUser, selectedFolder, sortConfig, userFoldersMap]);

  const typeOptions = [
    { label: "Word", icon: <MicrosoftWordIcon style={{ marginRight: 8 }} /> },
    { label: "PDF", icon: <PictureAsPdfIcon style={{ color: "#E32636", marginRight: 8 }} />, },
  ];

  // New handler for menu
  const handleMenuOpen = (event, document) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSelectedDocument(null);
  };

  const handleDelete = async (document) => {
    try {
      await axios.delete(`/api/documents/${document._id}`);
      setNotification({
        open: true,
        message: t('pages:documents.document_deleted'),
        severity: "success",
      });
      dispatch(getAllDocuments()); // Refresh the list
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || t('pages:documents.error_deleting'),
        severity: "error",
      });
    }
  };

  // Open move dialog and prepare available folders list
  const handleOpenMoveDialog = (document) => {
    try {
      setDocumentToMove(document);
      // Determine current case file id context
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      // Build folder options from redux folders for this owner and case file
      const ownerId = document.owner?._id;
      const backendFolders = (folders || []).filter(f => (
        f.owner?._id === ownerId && (!caseFileId || (f.caseFile && f.caseFile === caseFileId))
      ));

      // Ensure default folders exist as options
      const defaultNames = ['Correspondence', 'Financial', 'Medical', 'Other'];
      const defaults = defaultNames.map(name => ({ name, isDefault: true }));
      const combined = [...backendFolders.map(f => ({ name: f.name, isDefault: !!f.isDefault })), ...defaults];
      // Unique by name
      const seen = new Set();
      const unique = combined.filter(f => {
        if (seen.has(f.name)) return false;
        seen.add(f.name);
        return true;
      });
      setAvailableMoveFolders(unique.map(f => ({
        value: f.name,
        label: getTranslatedFolderName(f.name, f.isDefault),
      })));
      setMoveTargetFolder(document.folder || "");
      setMoveDialogOpen(true);
    } catch (e) {
      console.error('Error preparing move dialog', e);
    }
  };

  const handleConfirmMove = async () => {
    if (!documentToMove || !moveTargetFolder) {
      setMoveDialogOpen(false);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('folder', moveTargetFolder);
      await dispatch(updateDocument(documentToMove._id, formData));
      // Refresh lists
      dispatch(getAllDocuments());
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId));
      setNotification({ open: true, message: t('pages:documents.document_updated'), severity: 'success' });
    } catch (e) {
      console.error('Error moving document', e);
      setNotification({ open: true, message: t('pages:documents.error_moving') || 'Error moving document', severity: 'error' });
    } finally {
      setMoveDialogOpen(false);
      setDocumentToMove(null);
      setMoveTargetFolder("");
    }
  };

  const handleCloseMoveDialog = () => {
    setMoveDialogOpen(false);
    setDocumentToMove(null);
    setMoveTargetFolder("");
  };

  // Drag-and-drop handlers for documents table
  const handleTableDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.types?.includes('Files')) {
      setDragOverTable(true);
    }
  };

  const handleTableDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverTable(false);
  };

  const handleTableDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverTable(false);

    const filesList = Array.from(event.dataTransfer?.files || []);
    if (!filesList.length) return;

    setDroppedFiles(filesList);

    // Preselect case file: use currentFileId context when available
    let initialCaseFile = null;
    if (currentFileId) {
      initialCaseFile = filteredFilesForUpload.find(f => f._id === currentFileId) || null;
    }
    setUploadDialogCaseFile(initialCaseFile);

    // Preselect folder: currently selected folder (if not ALL) or default "Other"
    const initialFolder =
      selectedFolder && selectedFolder !== 'ALL'
        ? selectedFolder
        : 'Other';
    setUploadDialogFolder(initialFolder);

    setUploadDialogOpen(true);
  };

  const handleDropDialogClose = () => {
    if (uploadingFromDrop) return;
    setUploadDialogOpen(false);
    setDroppedFiles([]);
  };

  const handleConfirmDropUpload = async () => {
    if (!uploadDialogCaseFile || !droppedFiles.length || !user?.relationship) {
      setNotification({
        open: true,
        message: t('messages:error_uploading_documents'),
        severity: "error",
      });
      return;
    }

    try {
      setUploadingFromDrop(true);

      const formData = new FormData();
      formData.append("folder", uploadDialogFolder || "");
      formData.append("caseFile", uploadDialogCaseFile._id);
      formData.append("owner", user.relationship);
      formData.append("description", "");

      droppedFiles.forEach((file) => {
        formData.append("files", file);
        const baseTitle = file.name.replace(/\.[^/.]+$/, "");
        formData.append("titles", baseTitle);
      });

      await dispatch(uploadDocument(formData));

      setNotification({
        open: true,
        message: t('messages:document_uploaded_successfully'),
        severity: "success",
      });

      setUploadDialogOpen(false);
      setDroppedFiles([]);
      setUploadingFromDrop(false);

      // Refresh the document list
      dispatch(getAllDocuments());
    } catch (err) {
      console.error("Drag-and-drop upload error:", err);
      setNotification({
        open: true,
        message: err?.message || t('messages:error_uploading_documents'),
        severity: "error",
      });
      setUploadingFromDrop(false);
    }
  };

  const handleTitleClick = (document) => {
    setEditingDocumentId(document._id);
    setEditingTitle(document.title);
  };

  const handleTitleChange = (e) => {
    setEditingTitle(e.target.value);
  };

  const handleTitleUpdate = async () => {
    if (!editingDocumentId || editingTitle.trim() === "") return;
    
    // Get the current document
    const currentDocument = documents.find(doc => doc._id === editingDocumentId);
    
    // Check if the title has actually changed
    if (currentDocument && currentDocument.title === editingTitle.trim()) {
      setEditingDocumentId(null);
      setEditingTitle("");
      return;
    }

    try {
      await axios.put(`/api/documents/update/${editingDocumentId}`, { title: editingTitle });
      setNotification({
        open: true,
        message: t('pages:documents.title_updated'),
        severity: "success",
      });
      dispatch(getAllDocuments()); // Refresh the list
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || t('pages:documents.error_updating_title'),
        severity: "error",
      });
    }
    setEditingDocumentId(null);
    setEditingTitle("");
  };

  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTitleUpdate();
    } else if (e.key === 'Escape') {
      setEditingDocumentId(null);
      setEditingTitle("");
    }
  };

  const handleDescriptionClick = (document) => {
    setEditingDescriptionId(document._id);
    setEditingDescription(document.description || "");
  };

  const handleDescriptionChange = (e) => {
    setEditingDescription(e.target.value);
  };

  const handleDescriptionUpdate = async () => {
    if (!editingDescriptionId) return;
    
    // Get the current document
    const currentDocument = documents.find(doc => doc._id === editingDescriptionId);
    
    // Check if the description has actually changed
    if (currentDocument && currentDocument.description === editingDescription.trim()) {
      setEditingDescriptionId(null);
      setEditingDescription("");
      return;
    }

    try {
      await axios.put(`/api/documents/update/${editingDescriptionId}`, { description: editingDescription });
      setNotification({
        open: true,
        message: t('pages:documents.description_updated'),
        severity: "success",
      });
      dispatch(getAllDocuments()); // Refresh the list
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || t('pages:documents.error_updating_description'),
        severity: "error",
      });
    }
    setEditingDescriptionId(null);
    setEditingDescription("");
  };

  const handleDescriptionKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleDescriptionUpdate();
    } else if (e.key === 'Escape') {
      setEditingDescriptionId(null);
      setEditingDescription("");
    }
  };

  // Add handler for folder delete
  const handleDeleteFolder = async (folderObj) => {
    try {
      // Check if this is a virtual folder (default folder that doesn't exist in backend yet)
      if (folderObj._id && folderObj._id.startsWith('virtual-')) {
        // Virtual folders can't be deleted, show appropriate message
        setNotification({
          open: true,
          message: t('pages:documents.cannot_delete_default_folder'),
          severity: 'warning',
        });
        setDeleteFolderDialogOpen(false);
        setFolderToDelete(null);
        return;
      }

      await axios.delete(`/api/folders/${folderObj._id}`);
      setNotification({
        open: true,
        message: t('pages:documents.folder_deleted'),
        severity: 'success',
      });
      const caseFileId = currentFileId || (filterCaseFile ? filterCaseFile._id : null);
      dispatch(getAllFolders(currentLanguage || i18n.language, caseFileId));
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || t('pages:documents.error_deleting_folder'),
        severity: 'error',
      });
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
    }
  };

  if (error) return <div>Error: {error}</div>;

  return (
    <Box maxWidth ='xl'>
      <Box>
        <div
          style={{
            backgroundColor: "#ECECEC",
          }}
        >
          <Box sx={{ backgroundColor: "white", padding: "32px 24px" }}>
      {/* Only show Documents heading and upload button if not on file overview page */}
      {!currentFileId && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, mt: 2 }}>
          <BreadcrumbNav title={t('pages:documents.title')} />
          {props.hideUploadButton !== true && (
            <>
              <Button
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setUploadMenuAnchorEl(e.currentTarget)}
                size="small"
                sx={{
                  ...actionButtonStyles(),
                  minWidth: "auto",
                  whiteSpace: "nowrap"
                }}
              >
                {t('pages:documents.new_document')}
              </Button>
              <Menu
                anchorEl={uploadMenuAnchorEl}
                open={Boolean(uploadMenuAnchorEl)}
                onClose={() => setUploadMenuAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                  popper: {
                    modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
                  },
                }}
                PaperProps={{
                  sx: {
                    width: uploadMenuAnchorEl ? uploadMenuAnchorEl.offsetWidth : undefined,
                    minWidth: uploadMenuAnchorEl ? uploadMenuAnchorEl.offsetWidth : undefined,
                    maxWidth: uploadMenuAnchorEl ? uploadMenuAnchorEl.offsetWidth : undefined,
                    mt: 0,
                    boxShadow: 'none',
                    borderRadius: 0,
                    border: '1px solid #005b92',
                    py: 0.25,
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      py: 0.5,
                      px: 1.25,
                      color: '#005b92',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      '&:hover': {
                        backgroundColor: '#E8F4FC',
                      },
                    },
                  },
                }}
                MenuListProps={{
                  sx: { py: 0 },
                }}
              >
                <MenuItem onClick={() => {
                  navigate("/documents/upload");
                  setUploadMenuAnchorEl(null);
                }}>
                  {t('pages:documents.upload_document')}
                </MenuItem>
                <MenuItem onClick={() => {
                  navigate("/documents/upload-from-template");
                  setUploadMenuAnchorEl(null);
                }}>
                  {t('pages:documents.upload_from_template')}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2, width: "100%", mb: 2, alignItems: "center" }}>
        <TextField
          label={t('common:search')}
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          size="small"
          slotProps={{
            input: {
              startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
              sx: { padding: "0 10px", boxShadow: "none" },
            }
          }}
          sx={textFieldStyles({ width: "25%", marginRight: "0" })}
        />
        <Autocomplete
          options={typeOptions}
          getOptionLabel={(option) => option.label}
          value={typeOptions.find((option) => option.label === filterType) || null}
          onChange={handleFilterTypeChange}
          renderOption={(props, option) => <li {...props}>{option.icon}{option.label}</li>}
          renderInput={(params) => <TextField {...params} label={t('forms:labels.type')} size="small" sx={textFieldStyles({ width: "100%" })} />}
          sx={{ width: "25%" }}
        />

        <FormControl sx={{ width: "25%" }} size="small">
          <CustomAutocomplete
            options={relationships?.filter((rel) => rel.type === "Lawyer") || []}
            value={filterOwner}
            onChange={handleFilterOwnerChange}
            label={t('pages:documents.filter_by_owner')}
            filterType="Member"
            showIcon={true}
            icon={FilterListIcon}
            showAddNewOption={false}
          />
        </FormControl>

        {/* Only show Case filter if not on file overview page */}
        {!currentFileId && (
          <FileAutocomplete
            options={files || []}
            value={filterCaseFile}
            onChange={handleFilterCaseFileChange}
            label={t('common:case')}
            name="case"
            size="small"
            icon={<FilterListIcon />}
            width={"25%"}
          />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: "32px 24px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 0, height: "600px", border: "1px solid #d1d1d1", padding: 0, paddingLeft:0, paddingRight:0, overflowX: 'auto', overflowY: 'hidden' }}>
          {/* Left Panel - Hierarchical Users and Folders */}
          <Box sx={{ width: "200px", borderRight: "1px solid #d1d1d1", pr: 2, pl:2, pt:1, backgroundColor: "#FAFBFC" }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: "14px", fontWeight: 500 }}>
              {t('pages:documents.users_folders')}
            </Typography>
            <Box sx={{ maxHeight: "550px", overflowY: "auto" }}>
              {uniqueUsers.length > 0 ? (
                uniqueUsers.map((user) => (
                  <Box
                    key={user._id}
                    sx={{
                      mb: 1,
                      border: "1px solid #d1d1d1",
                      borderRadius: "0px",
                      backgroundColor: "white",
                      overflow: "hidden"
                    }}
                  >
                    {/* User Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 0.5,
                        cursor: "pointer",
                        backgroundColor: "white",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                        borderBottom: expandedUsers[user._id] ? "1px solid #d1d1d1" : "none"
                      }}
                      onClick={() => handleUserExpansion(user._id)}
                    >
                      <IconButton size="small" sx={{ mr: 1, p: 0.5 }}>
                        {expandedUsers[user._id] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                      </IconButton>
                      <Avatar
                        alt={`${user.firstName} ${user.lastName}`}
                        src={user.avatar || "/static/images/avatar/1.jpg"}
                        sx={{ width: 28, height: 28, mr: 1.5 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "13px" }}>
                          {user.businessName || `${user.firstName} ${user.lastName}`.trim()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "11px" }}>
                          {user.documentCount} {t('pages:documents.documents')}
                        </Typography>
                      </Box>
                      {/* { Add button of the loged in User Component} */}
                      {user?._id === loggedInUser &&
                      <Tooltip title={t('pages:documents.add_new_folder')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddFolder(user);
                          }}
                          sx={{ p: 0.5, color: "#2B80B6" }}
                        >
                          <AddIcon sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Tooltip>
                      }
                    </Box>

                    {/* Expandable Folders with Drag and Drop */}
                    <Collapse in={expandedUsers[user._id]}>
                      <Box sx={{ backgroundColor: "#fafafa" }}>
                        {userFoldersMap[user._id] && Object.keys(userFoldersMap[user._id]).length > 0 ? (
                          <DragDropContext onDragEnd={handleFolderDragEnd}>
                            <Droppable droppableId={`folders-${user._id}`}>
                              {(provided, snapshot) => (
                                <Box
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  sx={{
                                    backgroundColor: snapshot.isDraggingOver ? "#e3f2fd" : "transparent",
                                    borderRadius: "0px",
                                    transition: "background-color 0.2s ease",
                                    p: 1
                                  }}
                                >
                                  {/* "All" Option - Always show first above folders */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      p: 1,
                                      mb: 0.5,
                                      ml: 0,
                                      borderRadius: "0px",
                                      cursor: "pointer",
                                      backgroundColor: !selectedFolder && selectedUser?._id === user._id ? "#e3f2fd" : "white",
                                      "&:hover": {
                                        backgroundColor: !selectedFolder && selectedUser?._id === user._id ? "#e3f2fd" : "#f5f5f5",
                                      },
                                      border: "1px solid #e0e0e0"
                                    }}
                                    onClick={() => handleFolderSelect("ALL", user)}
                                  >
                                    <FolderIcon sx={{ mr: 1, color: "#2B80B6", fontSize: "18px" }} />
                                    <Typography variant="body2" sx={{ fontSize: "12px", flex: 1 }}>
                                      {t('common:all')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#666", fontSize: "10px" }}>
                                      {filteredDocuments.filter(doc => doc.owner._id === user._id).length}
                                    </Typography>
                                  </Box>

                                  {Object.entries(userFoldersMap[user._id]).map(([folderName, folderData], index) => {
                                    const { documents, folderObj } = folderData;
                                    return (
                                    <Draggable key={folderName} draggableId={`folder-${folderName}-${user._id}`} index={index}>
                                      {(provided, snapshot) => (
                                        <Box
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            p: 1,
                                            mb: 0.5,
                                            ml: 0,
                                            borderRadius: "0px",
                                            cursor: "pointer",
                                            backgroundColor: selectedFolder === folderName && selectedUser?._id === user._id ? "#e3f2fd" :
                                                           snapshot.isDragging ? "#e3f2fd" : "white",
                                            '&:hover .folder-delete-btn': { display: 'inline-flex' },
                                            '& .folder-delete-btn': { display: 'none' },
                                            "&:hover": {
                                              backgroundColor: selectedFolder === folderName && selectedUser?._id === user._id ? "#e3f2fd" : "#f5f5f5",
                                              "& .drag-handle": { display: "flex" }
                                            },
                                            boxShadow: snapshot.isDragging ? "0 4px 8px rgba(0,0,0,0.2)" : "none",
                                            transform: snapshot.isDragging ? "rotate(2deg)" : "none",
                                            transition: "all 0.2s ease",
                                            border: "1px solid #e0e0e0"
                                          }}
                                          onClick={() => handleFolderSelect(folderName, user)}
                                        >
                                          <Box
                                            className="drag-handle"
                                            {...provided.dragHandleProps}
                                            sx={{
                                              display: "none",
                                              mr: 1,
                                              cursor: "grab",
                                              "&:active": { cursor: "grabbing" }
                                            }}
                                          >
                                            <SixDots />
                                          </Box>
                                          <FolderIcon sx={{ mr: 1, color: "#2B80B6", fontSize: "18px" }} />
                                          <Typography variant="body2" sx={{ fontSize: "12px", flex: 1 }}>
                                            {getTranslatedFolderName(folderName, ['Financial', 'Correspondence', 'Medical', 'Other'].includes(folderName))}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: "#666", fontSize: "10px" }}>
                                            {documents.length}
                                          </Typography>
                                          {/* Delete icon, only if not default and folderObj exists and is not virtual */}
                                          {folderObj &&
                                           !['Medical', 'Correspondence', 'Financial', 'Other'].includes(folderObj.name) &&
                                           !folderObj._id?.startsWith('virtual-') &&
                                           user?._id === loggedInUser &&
                                            (
                                            <Tooltip title={t('pages:documents.delete_folder')}>
                                              <IconButton
                                                className="folder-delete-btn"
                                                size="small"
                                                sx={{ ml: 1, color: '#e53935',padding: "0px" }}
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  setFolderToDelete(folderObj);
                                                  setDeleteFolderDialogOpen(true);
                                                }}
                                              >
                                                <DeleteIcon sx={{ fontSize: "16px" }} />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Box>
                                      )}
                                    </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                </Box>
                              )}
                            </Droppable>
                          </DragDropContext>
                        ) : (
                          <Box sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="caption" sx={{ color: "#999", fontSize: "11px" }}>
                              {t('pages:documents.no_documents_found')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                ))
              ) : (
                <Box sx={{ ...smallTitle, p: "32px 24px", textAlign: "center" }}>{t('pages:documents.no_users_found')}</Box>
              )}
            </Box>
          </Box>

          {/* Right Panel - Documents */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: 0,
              height: '100%',
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              border: dragOverTable ? '2px dashed #2B80B6' : 'none',
              backgroundColor: dragOverTable ? '#f0f7ff' : 'transparent',
            }}
            onDragOver={handleTableDragOver}
            onDragLeave={handleTableDragLeave}
            onDrop={handleTableDrop}
          >
            {/* Documents Table Container */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {/* If no folder/user selected, show all documents table by default */}
              {!selectedFolder && !selectedUser ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0, backgroundColor: "#F5F7F9", pt:1, pb:1, pl:1, pr:1 }}>
                  <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 500 }}>
                    {t('pages:documents.all_documents')}
                  </Typography>
                  {selectedDocuments.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1,}}>
                      <Button
                        startIcon={<DownloadIcon />}
                        onClick={handleBulkDownload}
                        disabled={isDownloading}
                        variant="text"
                        sx={{
                          textTransform: "none",
                          color: "#2B80B6",
                          fontWeight: 400,
                          fontSize: "11px",
                          minWidth: "auto",
                          whiteSpace: "nowrap",
                          padding: "0px"  
                        }}
                      >
                        {isDownloading ? <CircularProgress size={16} /> : `${t('buttons:primary.download')} (${selectedDocuments.length})`}
                      </Button>
                        <IconButton
                        onClick={handleClearSelection}
                        size="small"
                        sx={{
                          color: "#666",
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          padding: "0px",
                          
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "16px",}} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <TableContainer sx={{ maxHeight: 500, overflowY: 'auto', width: '100%' }}>
                  <Table size="small">
                    <TableHead sx={{position: "sticky", top: 0, backgroundColor: "#ffffff", zIndex: 1000}}>
                      <TableRow sx={{backgroundColor: "#ffffff"}}>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                          onClick={() => handleSort('type')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('forms:labels.type')}
                            {sortConfig.key === 'type' && (
                              sortConfig.direction === 'asc' ?
                                <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> :
                                <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "15%",
                            cursor: 'pointer',
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                          onClick={() => handleSort('title')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('forms:labels.title')}
                            {sortConfig.key === 'title' && (
                              sortConfig.direction === 'asc' ?
                                <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> :
                                <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            width: "15%",
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          {t('forms:labels.description')}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            width: "12%",
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          {t('pages:files.file_number')}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            minWidth: "130px",
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          {t('pages:files.file_name')}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            minWidth: "100px",
                            cursor: 'pointer',
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                          onClick={() => handleSort('modifiedDate')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('forms:labels.modifiedDate')}
                            {sortConfig.key === 'modifiedDate' && (
                              sortConfig.direction === 'asc' ?
                                <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> :
                                <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0'}}>{t('common:actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {folderDocuments.map((document) => (
                        <TableRow
                          key={document._id}
                          onDoubleClick={() => handlePreviewDocument(document)}
                          sx={{
                            ...tableStyles.row,
                            backgroundColor: selectedDocuments.includes(document._id) ? "#e3f2fd" : "transparent",
                            borderBottom: '1px solid #d0d0d0',
                            cursor: 'pointer'
                          }}
                        >
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            verticalAlign: "middle"
                          }}>
                            {(() => {
                              const isImage =
                                (document.type && document.type.toLowerCase().startsWith('image/')) ||
                                (document.type && /\.(jpg|jpeg|png|gif|bmp|webp|tiff)$/i.test(document.type)) ||
                                (document.s3Key && /\.(jpg|jpeg|png|gif|bmp|webp|tiff)$/i.test(document.s3Key));
                              if (document.type === 'pdf') {
                                return <PictureAsPdfIcon sx={{ color: "#E32636", fontSize: '18px' }}/>;
                              } else if (isImage) {
                                return <ImageIcon sx={{ fontSize: '18px' }} />;
                              } else if (document.type === 'word') {
                                return <MicrosoftWordIcon sx={{ fontSize: '18px' }} />;
                              } else if (document.type === 'excel') {
                                return <ExcelIcon sx={{ fontSize: '18px' }} />;
                              } else {
                                return <DescriptionIcon sx={{ fontSize: '18px' }} />;
                              }
                            })()}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "200px",
                            minWidth: "200px", 
                            color: "#005b92",
                            verticalAlign: "middle"
                          }}>
                            {editingDocumentId === document._id ? (
                              <ClickAwayListener onClickAway={handleTitleUpdate}>
                                <TextField
                                  value={editingTitle}
                                  onChange={handleTitleChange}
                                  onKeyDown={handleTitleKeyPress}
                                  size="small"
                                  fullWidth
                                  autoFocus
                                  variant="standard"
                                  sx={{
                                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                                    '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' },
                                    '& .MuiInputBase-input': {
                                      fontSize: '12px',
                                      fontFamily: 'inherit',
                                      padding: '2px 0',
                                      verticalAlign: 'middle',
                                      color: "#005b92"
                                    }
                                  }}
                                />
                              </ClickAwayListener>
                            ) : (
                              <Tooltip title={t('pages:documents.rename_title')} arrow>
                                <Box
                                  component="span"
                                  onClick={() => handleTitleClick(document)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    color: "#005b92",
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#005b92'
                                    },
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: '100%'
                                  }}
                                >
                                  {document.title}
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{
                            ...tableStyles.cell,
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "200px",
                            minWidth: "200px",
                            maxWidth: "200px",
                            overflow: "hidden",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            lineHeight: "1.2",
                            maxHeight: "60px",
                            color: "#005b92",
                            verticalAlign: "middle"
                          }}>
                            {editingDescriptionId === document._id ? (
                              <ClickAwayListener onClickAway={handleDescriptionUpdate}>
                                <TextField
                                  value={editingDescription}
                                  onChange={handleDescriptionChange}
                                  onKeyDown={handleDescriptionKeyPress}
                                  size="small"
                                  fullWidth
                                  autoFocus
                                  multiline
                                  variant="standard"
                                  sx={{
                                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                                    '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' },
                                    '& .MuiInputBase-input': {
                                      fontSize: '12px',
                                      fontFamily: 'inherit',
                                      padding: '2px 0',
                                      verticalAlign: 'middle',
                                      color: "#005b92"
                                    }
                                  }}
                                />
                              </ClickAwayListener>
                            ) : (
                              <Tooltip title={t('pages:documents.edit_description')} arrow>
                                <Box
                                  component="span"
                                  onClick={() => handleDescriptionClick(document)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    color: "#005b92",
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#005b92'
                                    },
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: '100%'
                                  }}
                                >
                                  {document.description || '-'}
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "130px",
                            color: "#005b92",
                            verticalAlign: "middle"
                          }}>
                            {document.caseFile?.fileNo || '-'}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '4px 16px',
                            fontSize: '12px',
                            minWidth: "130px",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            "&:hover": { cursor: 'pointer', textDecoration: "underline" }, 
                            color: "#005b92",
                            verticalAlign: "middle"
                          }} 
                          onClick={() => document.caseFile?._id && navigate(`/files/${document.caseFile._id}/overview`)}>
                            {document.caseFile?.name || '-'}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '4px 16px',
                            fontSize: '12px',
                            minWidth: "130px",
                            whiteSpace: "normal",
                            wordWrap: "break-word"
                          }}>{formatDate(document.modifiedDate)}</TableCell>
                          <TableCell sx={{...tableStyles.cell, padding: '1px 16px', fontSize: '12px', cursor: 'pointer'}}>
                            <IconButton
                              aria-label="more"
                              aria-controls="document-menu"
                              aria-haspopup="true"
                              onClick={(event) => {
                                event.stopPropagation(); // Prevent double-click from firing
                                handleMenuOpen(event, document);
                              }}
                              sx={{ color: "#005b92", cursor: "pointer" }}
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              anchorEl={menuAnchorEl}
                              open={Boolean(menuAnchorEl)}
                              onClose={handleMenuClose}
                              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                              slotProps={{
                                popper: {
                                  modifiers: [{ name: 'offset', options: { offset: [-40, 0] } }],
                                },
                              }}
                              PaperProps={{
                                sx: {
                                  minWidth: 120,
                                  mt: 0,
                                  boxShadow: 'none',
                                  borderRadius: 0,
                                  border: '1px solid #005b92',
                                  py: 0.25,
                                  '& .MuiMenuItem-root': {
                                    fontSize: '12px',
                                    py: 0.5,
                                    px: 1.25,
                                    minHeight: 'auto',
                                    color: '#005b92',
                                    '&:hover': {
                                      backgroundColor: '#E8F4FC',
                                    },
                                  },
                                  '& .MuiListItemIcon-root': {
                                    minWidth: 24,
                                    color: '#005b92',
                                  },
                                },
                              }}
                              MenuListProps={{
                                sx: { py: 0 },
                              }}
                            >
                              <MenuItem onClick={() => {
                                handleView(menuSelectedDocument?._id, menuSelectedDocument?.type);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <VisibilityIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Open in new tab" sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handlePreviewDocument(menuSelectedDocument);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <PreviewIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('buttons:preview').charAt(0).toUpperCase() + t('buttons:preview').slice(1)} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleDownload(menuSelectedDocument?._id, menuSelectedDocument?.title);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <DownloadIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('buttons:primary.download')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleSendEmail(menuSelectedDocument?._id);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <SendIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('pages:emails.compose')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleOpenMoveDialog(menuSelectedDocument);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <DriveFileMoveIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('pages:documents.move')}
                                  sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleUpdateDocument(menuSelectedDocument?._id);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <EditIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('common:edit')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleDelete(menuSelectedDocument);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <DeleteIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('common:delete')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                            </Menu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {folderDocuments.length === 0 && (
              <Box sx={{ ...smallTitle, p: "32px 24px", textAlign: "center" }}>
                    {t('pages:documents.no_documents_found')}
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0, backgroundColor: "#F5F7F9", pt:1, pb:1, pl:1, pr:1 }}>
                  <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 500 }}>
                    {selectedFolder ? `${selectedFolder} Documents - ` : `${t('common:all')} Documents - `}{selectedUser?.businessName || `${selectedUser?.firstName} ${selectedUser?.lastName}`.trim()}
                    {selectedDocuments.length > 0 && (
                      <Typography component="span" sx={{ fontSize: "12px", color: "#666", ml: 1 }}>
                        ({selectedDocuments.length} selected)
                      </Typography>
                    )}
                  </Typography>
                  {selectedDocuments.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1,}}>
                      <Button
                        startIcon={<DownloadIcon />}
                        onClick={handleBulkDownload}
                        disabled={isDownloading}
                        variant="text"
                        sx={{
                          textTransform: "none",
                          color: "#2B80B6",
                          fontWeight: 400,
                          fontSize: "11.5px",
                          minWidth: "auto",
                          whiteSpace: "nowrap",
                          padding: "0px"  
                        }}
                      >
                        {isDownloading ? <CircularProgress size={16} /> : `${t('buttons:primary.download')} (${selectedDocuments.length})`}
                      </Button>
                        <IconButton
                        onClick={handleClearSelection}
                        size="small"
                        sx={{
                          color: "#666",
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          padding: "0px",
                          
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "16px",}} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <TableContainer sx={{ maxHeight: 530, overflowY: 'auto', width: '100%' }}>
                  <Table size="small">
                    <TableHead sx={{position: "sticky", top: 0, backgroundColor: "#ffffff", zIndex: 1000}}>
                      <TableRow sx={{backgroundColor: "#ffffff"}}>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                          onClick={() => handleSort('type')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('forms:labels.type')}
                            {sortConfig.key === 'type' && (
                              sortConfig.direction === 'asc' ?
                                <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> :
                                <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "15%",
                            cursor: 'pointer',
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                          onClick={() => handleSort('title')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('forms:labels.title')}
                            {sortConfig.key === 'title' && (
                              sortConfig.direction === 'asc' ?
                                <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> :
                                <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            width: "15%",
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          {t('forms:labels.description')}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            width: "12%",
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          {t('pages:files.file_number')}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            minWidth: "220px",
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          {t('pages:files.file_name')}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            fontWeight: 600,
                            padding: '4px 16px',
                            fontSize: '12px',
                            minWidth: "130px",
                            cursor: 'pointer',
                            borderBottom: '1px solid #d0d0d0',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                          onClick={() => handleSort('modifiedDate')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('forms:labels.modifiedDate')}
                            {sortConfig.key === 'modifiedDate' && (
                              sortConfig.direction === 'asc' ?
                                <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> :
                                <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0'}}>{t('common:actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {folderDocuments.map((document) => (
                        <TableRow
                          key={document._id}
                          onDoubleClick={() => handlePreviewDocument(document)}
                          sx={{
                            ...tableStyles.row,
                            backgroundColor: selectedDocuments.includes(document._id) ? "#e3f2fd" : "transparent",
                            borderBottom: '1px solid #d0d0d0',
                            cursor: 'pointer'
                          }}
                        >
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            verticalAlign: "middle"
                          }}>
                            {(() => {
                              const isImage =
                                (document.type && document.type.toLowerCase().startsWith('image/')) ||
                                (document.type && /\.(jpg|jpeg|png|gif|bmp|webp|tiff)$/i.test(document.type)) ||
                                (document.s3Key && /\.(jpg|jpeg|png|gif|bmp|webp|tiff)$/i.test(document.s3Key));
                              if (document.type === 'pdf') {
                                return <PictureAsPdfIcon sx={{ color: "#E32636" }} />;
                              } else if (isImage) {
                                return <ImageIcon />;
                              } else if (document.type === 'word') {
                                return <MicrosoftWordIcon />;
                              } else if (document.type === 'excel') {
                                return <ExcelIcon />;
                              } else {
                                return <DescriptionIcon />;
                              }
                            })()}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "200px",
                            minWidth: "200px", 
                            color: "#005b92",
                            verticalAlign: "middle"
                          }}>
                            {editingDocumentId === document._id ? (
                              <ClickAwayListener onClickAway={handleTitleUpdate}>
                                <TextField
                                  value={editingTitle}
                                  onChange={handleTitleChange}
                                  onKeyDown={handleTitleKeyPress}
                                  size="small"
                                  fullWidth
                                  autoFocus
                                  variant="standard"
                                  sx={{
                                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                                    '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' },
                                    '& .MuiInputBase-input': {
                                      fontSize: '12px',
                                      fontFamily: 'inherit',
                                      padding: '2px 0',
                                      verticalAlign: 'middle',
                                      color: "#005b92"
                                    }
                                  }}
                                />
                              </ClickAwayListener>
                            ) : (
                              <Tooltip title={t('pages:documents.rename_title')} arrow>
                                <Box
                                  component="span"
                                  onClick={() => handleTitleClick(document)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    color: "#005b92",
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#005b92'
                                    },
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: '100%'
                                  }}
                                >
                                  {document.title}
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{
                            ...tableStyles.cell,
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "200px",
                            minWidth: "200px",
                            maxWidth: "200px",
                            overflow: "hidden",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            lineHeight: "1.2",
                            maxHeight: "60px",
                            color: "#005b92",
                            verticalAlign: "middle"
                          }}>
                            {editingDescriptionId === document._id ? (
                              <ClickAwayListener onClickAway={handleDescriptionUpdate}>
                                <TextField
                                  value={editingDescription}
                                  onChange={handleDescriptionChange}
                                  onKeyDown={handleDescriptionKeyPress}
                                  size="small"
                                  fullWidth
                                  autoFocus
                                  multiline
                                  variant="standard"
                                  sx={{
                                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                                    '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' },
                                    '& .MuiInputBase-input': {
                                      fontSize: '12px',
                                      fontFamily: 'inherit',
                                      padding: '2px 0',
                                      verticalAlign: 'middle',
                                      color: "#005b92"
                                    }
                                  }}
                                />
                              </ClickAwayListener>
                            ) : (
                              <Tooltip title={t('pages:documents.edit_description')} arrow>
                                <Box
                                  component="span"
                                  onClick={() => handleDescriptionClick(document)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    color: "#005b92",
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#005b92'
                                    },
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: '100%'
                                  }}
                                >
                                  {document.description || '-'}
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "130px",
                            color: "#005b92",
                            verticalAlign: "middle"
                          }}>
                            {document.caseFile?.fileNo || '-'}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "130px",
                            "&:hover": { cursor: 'pointer', textDecoration: "underline" }, 
                            color: "#005b92",
                            verticalAlign: "middle"
                          }} 
                          onClick={() => document.caseFile?._id && navigate(`/files/${document.caseFile._id}/overview`)}>
                            {document.caseFile?.name || '-'}
                          </TableCell>
                          <TableCell sx={{ 
                            ...tableStyles.cell, 
                            padding: '0px 16px',
                            fontSize: '12px',
                            width: "110px",
                            minWidth: "100px", 
                          }}>{formatDate(document.modifiedDate)}</TableCell>
                          <TableCell sx={{...tableStyles.cell, padding: '0px 16px', fontSize: '11px', cursor: 'pointer'}}>
                            <IconButton
                              aria-label="more"
                              aria-controls="document-menu"
                              aria-haspopup="true"
                              onClick={(event) => {
                                event.stopPropagation(); // Prevent double-click from firing
                                handleMenuOpen(event, document);
                              }}
                              sx={{ color: "#005b92", cursor: "pointer" }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              anchorEl={menuAnchorEl}
                              open={Boolean(menuAnchorEl)}
                              onClose={handleMenuClose}
                              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                              slotProps={{
                                popper: {
                                  modifiers: [{ name: 'offset', options: { offset: [-40, 0] } }],
                                },
                              }}
                              PaperProps={{
                                sx: {
                                  minWidth: 120,
                                  mt: 0,
                                  boxShadow: 'none',
                                  borderRadius: 0,
                                  border: '1px solid #005b92',
                                  py: 0.25,
                                  '& .MuiMenuItem-root': {
                                    fontSize: '12px',
                                    py: 0.5,
                                    px: 1.25,
                                    minHeight: 'auto',
                                    color: '#005b92',
                                    '&:hover': {
                                      backgroundColor: '#E8F4FC',
                                    },
                                  },
                                  '& .MuiListItemIcon-root': {
                                    minWidth: 24,
                                    color: '#005b92',
                                  },
                                },
                              }}
                              MenuListProps={{
                                sx: { py: 0 },
                              }}
                            >
                              <MenuItem onClick={() => {
                                handleView(menuSelectedDocument?._id, menuSelectedDocument?.type);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <VisibilityIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Open in new tab" sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handlePreviewDocument(menuSelectedDocument);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <PreviewIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('buttons:preview').charAt(0).toUpperCase() + t('buttons:preview').slice(1)} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleDownload(menuSelectedDocument?._id, menuSelectedDocument?.title);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <DownloadIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('buttons:primary.download')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleSendEmail(menuSelectedDocument?._id);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <SendIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('pages:emails.compose')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleOpenMoveDialog(menuSelectedDocument);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <DriveFileMoveIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('pages:documents.move')}
                                  sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleUpdateDocument(menuSelectedDocument?._id);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <EditIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('common:edit')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                              <MenuItem onClick={() => {
                                handleDelete(menuSelectedDocument);
                                handleMenuClose();
                              }}>
                                <ListItemIcon>
                                  <DeleteIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('common:delete')} sx={{ fontSize: '13px', fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }} />
                              </MenuItem>
                            </Menu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {folderDocuments.length === 0 && (
                  <Box sx={{ ...smallTitle, p: "32px 24px", textAlign: "center" }}>
                    {t('pages:documents.no_documents_found')}
                  </Box>
                )}
              </Box>
            )}
            </Box>

            {/* Preview Sidebar */}
            <Box
              ref={previewSidebarRef}
              sx={{
                width: previewSidebarOpen ? '450px' : '0px',
                height: '100%',
                borderLeft: previewSidebarOpen ? '1px solid #d1d1d1' : 'none',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flexShrink: 0,
                transition: 'width 0.3s ease-in-out, border-left 0.3s ease-in-out',
                opacity: previewSidebarOpen ? 1 : 0,
                pointerEvents: previewSidebarOpen ? 'auto' : 'none'
              }}
            >
              {previewSidebarOpen && (
                <>
                  {/* Preview Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '4px 12px',
                      borderBottom: '1px solid #d1d1d1',
                      backgroundColor: '#F0F0F0',
                      minHeight: '30px',
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}
                    >
                      {previewDocument?.title || (t('buttons:preview').charAt(0).toUpperCase() + t('buttons:preview').slice(1))}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={handleRefreshPreview}
                        disabled={previewLoading}
                        sx={{
                          padding: '2px',
                          color: '#666',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                        title={t('common:refresh') || 'Refresh'}
                      >
                        <RefreshIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setPreviewSidebarOpen(false);
                          setPreviewDocument(null);
                          setPreviewUrl(null);
                          setPreviewError(false);
                        }}
                        sx={{
                          padding: '2px',
                          color: '#666',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Preview Content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'auto',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      minHeight: 0
                    }}
                  >
                    {previewLoading ? (
                      <CircularProgress size={24} />
                    ) : previewError || !previewUrl ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          color: '#999',
                          textAlign: 'center'
                        }}
                      >
                        {t('pages:documents.no_preview_available')}
                      </Typography>
                    ) : (
                      previewDocument?.type && previewDocument.type.startsWith('image') ? (
                        <img
                          src={previewUrl}
                          alt={previewDocument?.title || 'Preview'}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                          onError={() => setPreviewError(true)}
                        />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                          <iframe
                            src={previewUrl}
                            title="Document Preview"
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none'
                            }}
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                            onError={() => setPreviewError(true)}
                          />
                        </Box>
                      )
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <input type="file" ref={fileInputRef} style={{ display: "none" }} accept=".doc,.docx,.pdf" />

      {/* Add Folder Modal */}
      <Modal open={showAddFolderModal} onClose={handleCancelAddFolder}>
        <Box sx={{
          ...modalStyle,
          border: "2px solid rgb(180, 180, 180)",
          borderRadius: "0px"
        }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              mb: 2,
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: "16px",
              fontWeight: 600
            }}
          >
            {t('pages:documents.add_new_folder')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: "#666",
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: "14px",
              fontWeight: 400
            }}
          >
            {t('pages:documents.creating_folder_for')}: {addingFolderForUser?.businessName || `${addingFolderForUser?.firstName} ${addingFolderForUser?.lastName}`.trim()}
          </Typography>
          <TextField
            fullWidth
            label={t('forms:labels.folderName')}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            size="small"
            sx={{
              ...textFieldStyles("100%", "0", "24px", "0"),
              mb: 3,
              '& .MuiInputBase-input': {
                padding: '6px 8px', // reduce vertical and horizontal padding
                fontSize: '0.95rem', // slightly smaller font
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
              }
            }}
            autoFocus
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleCancelAddFolder}
              variant="outlined"
              sx={{
                borderRadius: "0px",
                textTransform: "none",
                fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                fontSize: "12px",
                fontWeight: 400,
                padding: "2px 20px",
                color: "#2B80B6",
                "&:hover": {
                  backgroundColor: "#f0f7ff",
                  borderColor: "#2C6B8C"
                }
              }}
            >
              {t('buttons:secondary.cancel')}
            </Button>
            <Button
              onClick={handleSaveNewFolder}
              variant="contained"
              disabled={!newFolderName.trim()}
              sx={{
                borderRadius: "0px",
                textTransform: "none",
                fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                fontSize: "12px",
                fontWeight: 400,
                padding: "2px 20px",
                backgroundColor: "#2B80B6",
                color: "white",
                boxShadow: "2px 2px 4px rgba(0,0,0,0.1), -2px -2px 4px rgba(255,255,255,0.7)",
                "&:hover": {
                  backgroundColor: "#2C6B8C",
                  boxShadow: "inset 4px 4px 8px #0561ad, inset -4px -4px 8px #0561ad"
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666"
                }
              }}
            >
              {t('buttons:primary.create')} {t('common:folder')}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Folder Delete Confirmation Dialog */}
      <Dialog open={deleteFolderDialogOpen} onClose={() => setDeleteFolderDialogOpen(false)}
  PaperProps={{
    sx: {
      borderRadius: 0,
      p: 2,
      background: '#f9fafb',
      minWidth: 380,
      boxShadow: '0 8px 32px rgba(44,62,80,0.12)',
    }
  }}
>
  {/* <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: '1.2rem', pb: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
      <DeleteIcon color="error" sx={{ fontSize: 28, mr: 0 }} />
    </Box>
    Delete Folder
  </DialogTitle> */}
  <DialogContent sx={{ pt: 1, pb: 2 }}>
    <DialogContentText sx={{ fontSize: '1rem', color: '#333', textAlign: 'center', mb: 1, mt:1 }}>
      {t('pages:documents.confirm_delete_folder', { folderName: folderToDelete?.name })}<br/>
      <span style={{ color: '#e53935', fontWeight: 500 }}>{t('pages:documents.delete_folder_warning')}</span><br/>
      {t('common:action_cannot_undone')}
    </DialogContentText>
  </DialogContent>
  <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
    <Button onClick={() => setDeleteFolderDialogOpen(false)} color="primary" sx={{ borderRadius: 0, px: 3, fontWeight: 500 }}>
      {t('buttons:cancel')}
    </Button>
    <Button onClick={() => handleDeleteFolder(folderToDelete)} color="error" variant="contained" sx={{ borderRadius: 0, px: 3, fontWeight: 500, boxShadow: '0 2px 8px rgba(229,57,53,0.08)' }} startIcon={<DeleteIcon />}>
      {t('common:delete')}
    </Button>
  </DialogActions>
</Dialog>

      {/* Move document dialog */}
      <Dialog open={moveDialogOpen} onClose={handleCloseMoveDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 500 }}>{t('pages:documents.move_title')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="move-folder-label">{t('pages:documents.select_folder')}</InputLabel>
            <Select
              labelId="move-folder-label"
              label={t('pages:documents.select_folder')}
              native
              value={moveTargetFolder}
              onChange={(e) => setMoveTargetFolder(e.target.value)}
            >
              <option aria-label="None" value="" />
              {availableMoveFolders.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMoveDialog} variant="outlined" sx={{ borderRadius: 0, textTransform: 'none' }}>{t('buttons:cancel')}</Button>
          <Button onClick={handleConfirmMove} variant="contained" sx={{ borderRadius: 0, textTransform: 'none' }}>{t('buttons:save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Drag-and-drop upload dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleDropDialogClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 500 }}>
          {t('pages:documents.upload_document')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {droppedFiles.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500 }}>
                  {t('pages:documents.files')} ({droppedFiles.length}):
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#555' }}>
                  {droppedFiles.map(f => f.name).join(', ')}
                </Typography>
              </Box>
            )}
            <FileAutocomplete
              options={filteredFilesForUpload}
              value={uploadDialogCaseFile}
              onChange={(event, newValue) => setUploadDialogCaseFile(newValue)}
              label={t('forms:labels.case_required')}
              name="caseFile"
            />
            <FormControl fullWidth size="small" variant="outlined" sx={textFieldStyles("100%", "0", "0", "0")}>
              <InputLabel>{t('forms:document_template.folder')}</InputLabel>
              <Select
                value={uploadDialogFolder}
                label={t('forms:document_template.folder')}
                onChange={(e) => setUploadDialogFolder(e.target.value)}
                MenuProps={{ PaperProps: { sx: { borderRadius: 0 } } }}
              >
                <MenuItem value="Correspondence">{t('forms:folders.correspondence')}</MenuItem>
                <MenuItem value="Financial">{t('forms:folders.financial')}</MenuItem>
                <MenuItem value="Medical">{t('forms:folders.medical')}</MenuItem>
                <MenuItem value="Other">{t('forms:folders.other')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDropDialogClose}
            variant="outlined"
            sx={{ borderRadius: 0, textTransform: 'none' }}
            disabled={uploadingFromDrop}
          >
            {t('buttons:cancel')}
          </Button>
          <Button
            onClick={handleConfirmDropUpload}
            variant="contained"
            sx={{ borderRadius: 0, textTransform: 'none' }}
            disabled={!uploadDialogCaseFile || !droppedFiles.length || uploadingFromDrop}
          >
            {uploadingFromDrop ? (
              <CircularProgress size={18} sx={{ color: 'white' }} />
            ) : (
              t('buttons:primary.upload')
            )}
          </Button>
        </DialogActions>
      </Dialog>

          </Box>
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification}>
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
        </div>
      </Box>
    </Box>
  );
};

export default DocumentList;
